/**
 * Vulnerability case of one product × CVE (DEV-132).
 *
 * - The CRA reporting clocks: 24 h early warning and 72 h notification run from
 *   the first confirmed AFFECTED, the final report 14 days from the corrective
 *   measure. Reports are submitted on the ENISA platform; each is prepared here
 *   field by field (DEV-60), and recording it keeps the moment and the content.
 *   Only that stamp meets a clock — a sent customer mail does not.
 * - The core facts, entered once: the authority report, the Security Advisory
 *   and the customer mail share them.
 * - The Security Advisory of the product (DEV-134), rendered from the core
 *   facts; customer mails are drafted only from the released advisory.
 * - What the CMDB derives: versions, member states, customers without a country.
 *
 * Missing facts are a hint, never a lock. The server owns every rule (422 with a
 * German detail).
 */
import type { MessageDescriptor } from '@lingui/core';
// Core `t` is aliased: the components below take their own `t` from useLingui().
import { msg, plural, t as translate } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Banner from '../../components/Banner';
import Chip, { ChipList, type ChipTone } from '../../components/Chip';
import Icon from '../../components/Icon';
import KeepHeight from '../../components/KeepHeight';
import { backendLabel } from '../../lib/backendLabels';
import { revokeCaseReport, setCaseCorrectiveMeasure, submitCaseReport, updateCaseFacts } from '../../lib/api';
import { fmtDate, fmtDay } from '../../lib/format';
import { intlLocale, translatedRecord } from '../../lib/locale';
import { missingPermissionTitle } from '../../lib/permissions';
import type {
  CaseAuthorityFields, CaseClock, CaseFacts, CaseFactsUpdate, CaseSensitivity, CaseStatus, ReportStage,
  VulnerabilityCase,
} from '../../lib/types';

import AuthorityReportPanel from './AuthorityReportPanel';
import CaseAdvisoryPanel from './CaseAdvisoryPanel';
import { CaseNav } from './CaseStepper';
import { defaultSection, type CaseSection } from './caseSteps';
import { clockUrgency, type ClockUrgency } from './clockUrgency';
import { STATUS_LABELS, STATUS_TONE } from './eventHelpers';

const CASE_STATUS_LABELS = translatedRecord<CaseStatus>({
  OPEN: msg`Offen`,
  COMPLETED: msg`Abgeschlossen`,
  OBSOLETE: msg`Entfallen`,
});

/** Labels are getters, so each read follows the active language. */
export const CASE_STATUS: Readonly<Record<CaseStatus, { label: string; tone: ChipTone }>> = {
  OPEN: { get label() { return CASE_STATUS_LABELS.OPEN; }, tone: 'info' },
  COMPLETED: { get label() { return CASE_STATUS_LABELS.COMPLETED; }, tone: 'good' },
  OBSOLETE: { get label() { return CASE_STATUS_LABELS.OBSOLETE; }, tone: 'idle' },
};

const SENSITIVITY_LABELS: Readonly<Record<CaseSensitivity, MessageDescriptor>> = {
  CLEAR: msg`TLP:CLEAR — uneingeschränkt`,
  GREEN: msg`TLP:GREEN — Community`,
  AMBER: msg`TLP:AMBER — begrenzter Kreis`,
  AMBER_STRICT: msg`TLP:AMBER+STRICT — nur Empfängerorganisation`,
  RED: msg`TLP:RED — nur benannte Empfänger`,
};

const FACT_FIELDS: ReadonlyArray<{ key: keyof CaseFacts; label: MessageDescriptor; hint: MessageDescriptor }> = [
  { key: 'summary', label: msg`Zusammenfassung`, hint: msg`Was ist die Schwachstelle? Vorbelegt aus der CVE-Beschreibung.` },
  { key: 'exploitation_details', label: msg`Art der Ausnutzung`, hint: msg`Wie wird sie ausgenutzt? Vorbelegt aus den KEV-Angaben.` },
  { key: 'impact', label: msg`Auswirkung und Schweregrad`, hint: msg`Was bedeutet sie für dieses Produkt?` },
  { key: 'user_mitigations', label: msg`Maßnahmen für Nutzer`, hint: msg`Was können Kunden selbst tun, bis das Update da ist?` },
  { key: 'corrective_measures', label: msg`Korrekturmaßnahmen`, hint: msg`Was wurde ergriffen? Sicherheitsupdate, behobene Version.` },
];

type FactValues = CaseFacts & CaseAuthorityFields;

function hoursLabel(hours: number): string {
  const value = Math.abs(hours);
  if (value >= 48) return translate`${plural(Math.round(value / 24), { one: '# Tag', other: '# Tage' })}`;
  const amount = value.toLocaleString(intlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return translate`${amount} h`;
}

/** The product of a case by name, its id when the name is missing. */
function caseProductName(item: Pick<VulnerabilityCase, 'product_name' | 'product_type_id'>): string {
  const productTypeId = item.product_type_id ?? '?';
  return item.product_name ?? translate`Produkt #${productTypeId}`;
}

/** DEV-103: yellow from 72 h, red from 24 h left. */
const URGENCY_TONE: Readonly<Record<ClockUrgency, ChipTone>> = { 0: 'neutral', 1: 'warn', 2: 'warn', 3: 'alert', 4: 'alert' };

/** State of one reporting clock as a chip; shared with the page „Meldefristen“. */
export function clockChip(clock: CaseClock): { label: string; tone: ChipTone } {
  const hours = clock.remaining_hours ?? 0;
  switch (clock.state) {
    case 'submitted':
      return {
        label: clock.on_time === false ? translate`Gemeldet, nach Frist` : translate`Gemeldet`,
        tone: clock.on_time === false ? 'warn' : 'good',
      };
    case 'overdue': {
      const duration = hoursLabel(hours);
      return { label: translate`Seit ${duration} überschritten`, tone: 'alert' };
    }
    case 'waiting':
      return { label: translate`Wartet auf Abhilfemaßnahme`, tone: 'idle' };
    default: {
      const duration = hoursLabel(hours);
      return { label: translate`Noch ${duration}`, tone: URGENCY_TONE[clockUrgency(clock)] };
    }
  }
}

/** Now as a `datetime-local` value in the browser's time zone. */
function localNow(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/* ------------------------------------------------------------------------- *
 * Tab: one case per product of the hit, the selected one beside the list
 * ------------------------------------------------------------------------- */

export function CaseTab({ cases, revision, productTypeId, onSelect, views, onViewsChange, ...panel }: Readonly<
  Omit<CasePanelProps, 'item' | 'view' | 'onViewChange'> & {
    cases: readonly VulnerabilityCase[];
    /** Remounts the forms with the server state after every save. */
    revision: number;
    /** The product whose case is shown; the first one when unknown. */
    productTypeId: number | null;
    onSelect: (productTypeId: number | null) => void;
    /** The view per case, kept by the dialog above the panel that a save rebuilds. */
    views: Readonly<Record<number, CaseView>>;
    onViewsChange: (caseId: number, view: CaseView) => void;
  }
>): ReactNode {
  const { t } = useLingui();
  const selected = cases.find((item) => item.product_type_id === productTypeId) ?? cases[0] ?? null;
  function optionLabel(entry: VulnerabilityCase): string {
    const name = caseProductName(entry);
    if (!entry.next_deadline) return name;
    const deadline = fmtDate(entry.next_deadline);
    return t`${name} · nächste Frist ${deadline}`;
  }
  if (selected === null) {
    return (
      <p className="muted">
        <Trans>
          Ein Schwachstellenfall entsteht mit der ersten bestätigten Produktbewertung „Betroffen“. Er trägt die
          Kernangaben und die Meldefristen an die Behörde.
        </Trans>
      </p>
    );
  }
  // Several cases of one hit: the choice sits above the case navigation, so no
  // third column opens beside it.
  const picker = cases.length > 1 ? (
    <label className="case-picker">
      <span className="case-nav-group"><Trans>Produkt</Trans></span>
      <select value={selected.id}
        onChange={(e) => onSelect(cases.find((entry) => entry.id === Number(e.target.value))?.product_type_id ?? null)}>
        {cases.map((entry) => (
          <option key={entry.id} value={entry.id}>{optionLabel(entry)}</option>
        ))}
      </select>
    </label>
  ) : null;
  return (
    <KeepHeight resetKey={selected.id}>
      <CasePanel key={`case-${selected.id}-${revision}`} item={selected} {...panel} picker={picker}
        view={views[selected.id]} onViewChange={(view) => onViewsChange(selected.id, view)} />
    </KeepHeight>
  );
}

/* ------------------------------------------------------------------------- *
 * Panel
 * ------------------------------------------------------------------------- */

export interface CasePanelProps {
  item: VulnerabilityCase;
  canWrite: boolean;
  saving: boolean;
  /** Runs a change with the dialog's toast, reload and error banner. */
  onRun: (action: () => Promise<string>) => void;
  onDirtyChange: (dirty: boolean) => void;
  /** The report to open right away, e.g. from the deadline view (DEV-60). */
  openStage?: ReportStage | null;
  /** The work area and stage chosen before; kept by the tab across the reload after a save. */
  view?: CaseView;
  /** The choice of the case when a hit has several; replaces the heading. */
  picker?: ReactNode;
  onViewChange?: (view: CaseView) => void;
}

export interface CaseView {
  section: CaseSection;
  /** The report stage open in „Meldungen“; the next deadline when unset. */
  stage?: ReportStage;
}

/** The clock to report next: the earliest running deadline without a stamp. */
function nextClock(clocks: readonly CaseClock[]): string | null {
  const open = clocks.filter((clock) => !clock.submitted_at && clock.due && clock.state !== 'waiting');
  open.sort((a, b) => (a.due ?? '').localeCompare(b.due ?? ''));
  return open[0]?.key ?? null;
}

export default function CasePanel({
  item, canWrite, saving, onRun, onDirtyChange, openStage = null, view, onViewChange, picker = null,
}: Readonly<CasePanelProps>): ReactNode {
  const { t } = useLingui();
  const status = CASE_STATUS[item.status];
  const nextKey = nextClock(item.clocks);
  const next = item.clocks.find((clock) => clock.key === nextKey) ?? null;
  // DEV-103: one work area at a time, chosen in the step bar. The dialog keeps
  // the choice (`view`): across the reload after a save, and when a step below
  // a product verdict opens the case at that very place.
  const fallbackStage = openStage ?? next?.stage ?? item.clocks[0]?.stage ?? 'early_warning';
  const [section, setSectionState] = useState<CaseSection>(
    view?.section ?? (openStage ? 'reports' : defaultSection(item)));
  const [stage, setStageState] = useState<ReportStage>(view?.stage ?? fallbackStage);
  useEffect(() => {
    if (view === undefined) return;
    setSectionState(view.section);
    if (view.stage) setStageState(view.stage);
  }, [view]);
  function setSection(chosen: CaseSection): void {
    setSectionState(chosen);
    onViewChange?.({ section: chosen, stage });
  }
  /** A report stage in the navigation: area and stage in one step, so neither undoes the other. */
  function openReport(chosen: ReportStage): void {
    setStageState(chosen);
    setSectionState('reports');
    onViewChange?.({ section: 'reports', stage: chosen });
  }
  const clock = item.clocks.find((entry) => entry.stage === stage) ?? null;
  // Core facts and advisory are edited apart; either keeps the dialog open.
  const [factsDirty, setFactsDirty] = useState(false);
  const [advisoryDirty, setAdvisoryDirty] = useState(false);
  useEffect(() => onDirtyChange(factsDirty || advisoryDirty), [factsDirty, advisoryDirty, onDirtyChange]);
  const onFactsDirty = useCallback((dirty: boolean) => setFactsDirty(dirty), []);
  const onAdvisoryDirty = useCallback((dirty: boolean) => setAdvisoryDirty(dirty), []);
  const productName = item.product_name ?? '';
  const awareSince = fmtDate(item.aware_since);
  const factsUpdatedAt = item.facts_updated_at ? fmtDate(item.facts_updated_at) : null;
  const factsUpdatedBy = item.facts_updated_by ?? t`unbekannt`;

  return (
    <section className="triage-doc-panel case-panel" aria-label={t`Schwachstellenfall ${productName}`}>
      {item.contradiction ? (
        <Banner kind="error">
          <Trans>
            An die Behörde gemeldet, aber keine Version ist mehr als „Betroffen“ bestätigt. Die erfassten Meldungen
            bleiben bestehen.
          </Trans>
        </Banner>
      ) : null}
      {item.status === 'OBSOLETE' ? (
        <Banner kind="info"><Trans>Entfallen: Keine Version ist mehr betroffen, und gemeldet wurde nichts.</Trans></Banner>
      ) : null}

      <div className="case-layout">
        <CaseNav item={item} section={section} stage={stage} unsaved={{ facts: factsDirty, advisory: advisoryDirty }}
          onSection={setSection} onReport={openReport}
          header={picker ?? (
            <div className="case-nav-head">
              <h3>{caseProductName(item)}</h3>
              {/* An open case is the normal state; only a closed one is worth a chip. */}
              {item.status === 'OPEN' ? null : <Chip tone={status.tone}>{status.label}</Chip>}
            </div>
          )} />

        <div className="case-content">
          <div id={`case-${item.id}-section-reports`} hidden={section !== 'reports'}>
            {clock ? (
              <p className="hint case-stage-due">
                <StageDue clock={clock} />
                {' '}<Trans>· Fristen ab Kenntnis am {awareSince}</Trans>
              </p>
            ) : null}
            {/* Stays mounted while hidden, like the other areas: no reload when switching back. */}
            {clock ? (
              <StageView item={item} clock={clock}
                canWrite={canWrite} saving={saving} onRun={onRun} onEditFacts={() => setSection('facts')} />
            ) : null}
            <p className="hint case-reports-foot">
              <Trans>Eine Frist gilt erst mit der Meldung über die ENISA-Plattform als erfüllt.</Trans>
              {item.srp_reference ? <> <Trans>SRP-Referenz: <span className="mono">{item.srp_reference}</span>.</Trans></> : null}
              {' '}<Link to={`/?view=fristen&case=${item.id}`}><Trans>Alle Fristen im Dashboard</Trans></Link>
            </p>
          </div>
          <div id={`case-${item.id}-section-facts`} hidden={section !== 'facts'}>
            <p className="hint">
              <Trans>
                Einmal erfasst für Behördenmeldung, Security Advisory und Kundenmail. Eine Änderung nimmt die Freigabe des
                Advisorys zurück.
              </Trans>
              {factsUpdatedAt ? ' ' + t`Zuletzt ${factsUpdatedAt} · ${factsUpdatedBy}.` : ''}
            </p>
            <FactsForm item={item} canWrite={canWrite} saving={saving} onRun={onRun} onDirtyChange={onFactsDirty} />
            <DerivedFacts item={item} />
          </div>
          <div id={`case-${item.id}-section-advisory`} hidden={section !== 'advisory'}>
            <CaseAdvisoryPanel caseId={item.id} canWrite={canWrite} saving={saving} onRun={onRun}
              onDirtyChange={onAdvisoryDirty} onOpenMails={() => setSection('mails')} />
          </div>
          <div id={`case-${item.id}-section-mails`} hidden={section !== 'mails'}>
            <MailsSection item={item} onOpenAdvisory={() => setSection('advisory')} />
          </div>
        </div>
      </div>
    </section>
  );
}

/** The deadline of one report stage, or why it has not started yet. */
function StageDue({ clock }: Readonly<{ clock: CaseClock }>): ReactNode {
  const label = backendLabel(clock.label);
  if (clock.due) {
    const due = fmtDate(clock.due);
    return <Trans>{label}: fällig {due}</Trans>;
  }
  return <Trans>{label}: die Frist beginnt mit der Abhilfemaßnahme</Trans>;
}

/* ------------------------------------------------------------------------- *
 * Step 3: the customer mails, queued from the released advisory
 * ------------------------------------------------------------------------- */

function MailsSection({ item, onOpenAdvisory }: Readonly<{ item: VulnerabilityCase; onOpenAdvisory: () => void }>): ReactNode {
  const { t } = useLingui();
  const { total, sent } = item.mails;
  if (item.advisory.status !== 'RELEASED') {
    return (
      <Banner kind="info" sub={<button type="button" className="ghost small" onClick={onOpenAdvisory}><Trans>Zum Security Advisory</Trans></button>}>
        {total
          ? t`Die Kundenmails entstehen aus dem freigegebenen Security Advisory. Erst das Advisory freigeben, dann lassen sich die ${plural(total, { one: '# Mail', other: '# Mails' })} im Postausgang einreihen.`
          : t`Die Kundenmails entstehen aus dem freigegebenen Security Advisory. Erst das Advisory freigeben, dann lassen sich die Mails im Postausgang einreihen.`}
      </Banner>
    );
  }
  if (total === 0) return <p className="hint"><Trans>Für diesen Fall gibt es keine Kundenmails.</Trans></p>;
  return (
    <div className="case-mails">
      <p>
        <Trans><strong>{sent} von {total}</strong> Kundenmails versendet.</Trans>
        {sent < total ? ' ' + t`Im Postausgang einreihen und versenden; jede Mail geht an einen Kunden.` : ''}
      </p>
      <Link className={sent < total ? 'link-button primary' : 'link-button'} to={`/benachrichtigungen?case=${item.id}`}>
        <Icon name="mail" size={14} /> {sent < total ? t`Kundenmails einreihen` : t`Kundenmails ansehen`}
      </Link>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * One report stage: what is missing, the SRP fields, recording it
 * ------------------------------------------------------------------------- */

function StageView({ item, clock, canWrite, saving, onRun, onEditFacts }: Readonly<{
  item: VulnerabilityCase;
  clock: CaseClock;
  canWrite: boolean;
  saving: boolean;
  onRun: CasePanelProps['onRun'];
  onEditFacts: () => void;
}>): ReactNode {
  const { t } = useLingui();
  // The record form belongs to one stage; switching stages keeps the view mounted (no flicker).
  const [recordingKey, setRecordingKey] = useState<string | null>(null);
  const recording = recordingKey === clock.key;
  const [at, setAt] = useState(localNow);
  const [reference, setReference] = useState('');
  const locked = !canWrite || saving;
  const disabledTitle = canWrite ? undefined : missingPermissionTitle('triage.write');
  const formId = `case-${item.id}-${clock.key}`;
  const label = backendLabel(clock.label);

  function record(formEvent: FormEvent): void {
    formEvent.preventDefault();
    onRun(async () => {
      await submitCaseReport(item.id, clock.stage, {
        submitted_at: at ? new Date(at).toISOString() : null, srp_reference: reference,
      });
      return t`${label} als gemeldet erfasst.`;
    });
  }

  function revoke(): void {
    if (!window.confirm(t`Erfasste ${label} zurücknehmen? Die Frist läuft dann wieder.`)) return;
    onRun(async () => {
      await revokeCaseReport(item.id, clock.stage);
      return t`${label} zurückgenommen.`;
    });
  }

  const action = clock.submitted_at ? (
    <button type="button" className="ghost" disabled={locked} title={disabledTitle} onClick={revoke}>
      <Trans>Meldung zurücknehmen</Trans>
    </button>
  ) : (
    <button type="button" className="primary" disabled={locked || item.status === 'OBSOLETE' || recording}
      title={disabledTitle} aria-controls={formId} onClick={() => setRecordingKey(clock.key)}>
      <Trans>Als gemeldet erfassen</Trans>
    </button>
  );

  return (
    <div className="case-stage-view">
      {clock.stage === 'final_report' && !clock.submitted_at ? (
        <CorrectiveMeasure item={item} locked={locked} onRun={onRun} />
      ) : null}
      <AuthorityReportPanel id={`${formId}-report`} caseId={item.id} stage={clock.stage} actions={action}
        onEditFacts={onEditFacts} />
      {recording ? (
        <form id={formId} className="case-report-form" onSubmit={record}>
          <div className="field">
            <label htmlFor={`${formId}-at`}><Trans>Gemeldet am</Trans></label>
            <input id={`${formId}-at`} type="datetime-local" value={at} max={localNow()}
              onChange={(e) => setAt(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor={`${formId}-ref`}><Trans>SRP-Referenz (optional)</Trans></label>
            <input id={`${formId}-ref`} value={reference} maxLength={200} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="assessment-form-actions">
            <div className="spacer" />
            <button type="button" className="ghost" onClick={() => setRecordingKey(null)}><Trans>Abbrechen</Trans></button>
            <button type="submit" className="primary" disabled={saving}><Trans>Erfassen</Trans></button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Corrective measure: starts the 14-day final report clock
 * ------------------------------------------------------------------------- */

function CorrectiveMeasure({ item, locked, onRun }: Readonly<{
  item: VulnerabilityCase;
  locked: boolean;
  onRun: CasePanelProps['onRun'];
}>): ReactNode {
  const { t } = useLingui();
  const current = item.corrective_measure.available_at?.slice(0, 10) ?? '';
  const recordedBy = item.corrective_measure.recorded_by;
  const [day, setDay] = useState(current);
  const id = `case-${item.id}-measure`;

  function save(formEvent: FormEvent): void {
    formEvent.preventDefault();
    onRun(async () => {
      await setCaseCorrectiveMeasure(item.id, day);
      const due = fmtDay(addDays(day, 14));
      return t`Abhilfemaßnahme erfasst: Der Abschlussbericht ist bis ${due} fällig.`;
    });
  }

  function clear(): void {
    onRun(async () => {
      await setCaseCorrectiveMeasure(item.id, null);
      return t`Abhilfemaßnahme entfernt.`;
    });
  }

  return (
    <form className="case-measure" onSubmit={save}>
      <fieldset disabled={locked}>
        <label htmlFor={id}><Trans>Abhilfemaßnahme verfügbar seit</Trans></label>
        <input id={id} type="date" value={day} max={localNow().slice(0, 10)} onChange={(e) => setDay(e.target.value)} />
        <button type="submit" className="ghost small" disabled={!day || day === current}><Trans>Speichern</Trans></button>
        {current ? <button type="button" className="ghost small" onClick={clear}><Trans>Entfernen</Trans></button> : null}
        <span className="hint">
          <Trans>Update oder Workaround für die Nutzer; ab diesem Tag läuft die 14-Tage-Frist.</Trans>
          {recordedBy ? ' ' + t`Erfasst von ${recordedBy}.` : ''}
        </span>
      </fieldset>
    </form>
  );
}

function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString();
}

/* ------------------------------------------------------------------------- *
 * Core facts and the authority-only fields
 * ------------------------------------------------------------------------- */

function initialValues(item: VulnerabilityCase): FactValues {
  return { ...item.facts, ...item.authority };
}

function changedValues(values: FactValues, initial: FactValues): CaseFactsUpdate {
  const keys = Object.keys(values) as Array<keyof FactValues>;
  return Object.fromEntries(keys.filter((key) => values[key] !== initial[key]).map((key) => [key, values[key]]));
}

function FactsForm({ item, canWrite, saving, onRun, onDirtyChange }: Readonly<CasePanelProps>): ReactNode {
  const { t, i18n } = useLingui();
  const initial = useMemo(() => initialValues(item), [item]);
  const [values, setValues] = useState<FactValues>(initial);
  const changes = changedValues(values, initial);
  const dirty = Object.keys(changes).length > 0;
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  const idBase = `case-${item.id}`;

  function set<K extends keyof FactValues>(key: K, value: FactValues[K]): void {
    setValues((current) => ({ ...current, [key]: value }));
  }

  function save(formEvent: FormEvent): void {
    formEvent.preventDefault();
    onRun(async () => {
      await updateCaseFacts(item.id, changes);
      return t`Kernangaben gespeichert.`;
    });
  }

  return (
    <form className="assessment-form" onSubmit={save}>
      <fieldset disabled={!canWrite || saving}>
        {FACT_FIELDS.map((field) => (
          <div className="field" key={field.key}>
            <label htmlFor={`${idBase}-${field.key}`}>{i18n._(field.label)}</label>
            <textarea id={`${idBase}-${field.key}`} rows={3} value={values[field.key]}
              placeholder={i18n._(field.hint)} onChange={(e) => set(field.key, e.target.value)} />
          </div>
        ))}
        <span className="assessment-environment-label"><Trans>Nur für die Behördenmeldung</Trans></span>
        <div className="field">
          <label htmlFor={`${idBase}-sensitivity`}><Trans>Sensibilität der Angaben</Trans></label>
          <select id={`${idBase}-sensitivity`} value={values.sensitivity ?? ''}
            onChange={(e) => set('sensitivity', (e.target.value || null) as CaseSensitivity | null)}>
            <option value="">{t`— bitte wählen —`}</option>
            {(Object.keys(SENSITIVITY_LABELS) as CaseSensitivity[]).map((code) => (
              <option key={code} value={code}>{i18n._(SENSITIVITY_LABELS[code])}</option>
            ))}
          </select>
        </div>
        <label className="check">
          <input type="checkbox" checked={values.special_circumstances}
            onChange={(e) => set('special_circumstances', e.target.checked)} />
          <Trans>Besondere Umstände nach Art. 16 (ENISA erhält zunächst eingeschränkte Angaben)</Trans>
        </label>
        {values.special_circumstances ? (
          <div className="field">
            <label htmlFor={`${idBase}-special`}><Trans>Begründung der besonderen Umstände</Trans></label>
            <textarea id={`${idBase}-special`} rows={2} value={values.special_circumstances_note}
              onChange={(e) => set('special_circumstances_note', e.target.value)} />
          </div>
        ) : null}
        <div className="field">
          <label htmlFor={`${idBase}-actor`}><Trans>Angaben zum Angreifer</Trans></label>
          <textarea id={`${idBase}-actor`} rows={2} value={values.threat_actor_info}
            placeholder={t`Für den Abschlussbericht, soweit bekannt.`} onChange={(e) => set('threat_actor_info', e.target.value)} />
        </div>
      </fieldset>
      <div className="assessment-form-actions">
        <div className="spacer" />
        <button type="submit" className="primary" disabled={!canWrite || saving || !dirty}
          title={canWrite ? undefined : missingPermissionTitle('triage.write')}>
          <Trans>Kernangaben speichern</Trans>
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------- *
 * Derived from the assessments and the master data
 * ------------------------------------------------------------------------- */

function DerivedFacts({ item }: Readonly<{ item: VulnerabilityCase }>): ReactNode {
  const { t } = useLingui();
  function verdictLabel(version: VulnerabilityCase['versions'][number]): string {
    const status = STATUS_LABELS[version.status];
    return version.confirmed ? status : t`Vorschlag: ${status}`;
  }
  return (
    <details className="triage-context-details">
      <summary><Trans>Abgeleitet aus Bewertungen und Stammdaten</Trans></summary>
      <dl className="detail-list">
        <dt><Trans>Versionen</Trans></dt>
        <dd>
          <ChipList>
            {item.versions.map((version) => (
              <Chip key={version.version_id} tone={version.confirmed ? STATUS_TONE[version.status] : 'neutral'}>
                {version.version_nr ?? `#${version.version_id}`}
                {' · '}
                {verdictLabel(version)}
              </Chip>
            ))}
          </ChipList>
        </dd>
        <dt><Trans>Mitgliedstaaten</Trans></dt>
        <dd>
          {item.member_states.length > 0
            ? item.member_states.map((state) => `${state.name} (${state.iso})`).join(', ')
            : t`Kein betroffener Kunde mit EU-Land`}
        </dd>
        {item.customers_without_country.length > 0 ? (
          <>
            <dt><Trans>Kunden ohne Land</Trans></dt>
            <dd>
              {item.customers_without_country.join(', ')}
              <span className="hint"> <Trans>— Land in den Stammdaten ergänzen, sonst fehlen sie in der Frühwarnung.</Trans></span>
            </dd>
          </>
        ) : null}
      </dl>
    </details>
  );
}
